/**
 * Presentación de horarios (008 · US1). La UI no calcula reglas (wiring §11.4): solo formatea lo que
 * devuelve la API. Fechas `LocalDate` (`YYYY-MM-DD`) y horas `LocalTime` (`HH:mm`, 24 h) se tratan
 * como texto local de la sede, sin convertir zonas; "hoy" se calcula en la zona de la sede.
 */
import { localDayDiff, type ShiftSummary } from '@fermin/contracts';

import { t } from '../../i18n';
import { localDateInZone } from '../attendance/local-datetime';

export { formatLocalDay } from '../regularizations/format';

/** `08:00` + `17:00` → `08:00–17:00`. */
export function timeRange(start: string, end: string): string {
  return `${start}–${end}`;
}

/** `480` → `8 h`; `510` → `8 h 30 min`; `45` → `45 min`. */
export function durationLabel(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (hours === 0) {
    return t('schedules.format.minutes', { minutes: rest });
  }
  if (rest === 0) {
    return t('schedules.format.hours', { hours });
  }
  return t('schedules.format.hoursMinutes', { hours, minutes: rest });
}

/** Código visible de una celda con turnos: `M`, `M+T`. */
export function shiftsCode(shifts: readonly Pick<ShiftSummary, 'code'>[]): string {
  return shifts.map((shift) => shift.code).join('+');
}

/** "Mañana 08:00–17:00 y Tarde 18:00–22:00". */
export function shiftsDescription(
  shifts: readonly Pick<ShiftSummary, 'name' | 'start_time' | 'end_time'>[],
): string {
  return shifts
    .map((shift) => `${shift.name} ${timeRange(shift.start_time, shift.end_time)}`)
    .join(t('schedules.format.and'));
}

function parseDay(day: string): Date {
  return new Date(`${day}T00:00:00Z`);
}

function toDay(date: Date): string {
  return date.toISOString().slice(0, 10);
}

/** `YYYY-MM-DD` + n días (sobre medianoches UTC: no depende de la zona del navegador). */
export function addDays(day: string, days: number): string {
  const date = parseDay(day);
  date.setUTCDate(date.getUTCDate() + days);
  return toDay(date);
}

/** Primer y último día del mes de `day`. */
export function monthBounds(day: string): { from: string; to: string } {
  const date = parseDay(day);
  const first = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1));
  const last = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 0));
  return { from: toDay(first), to: toDay(last) };
}

/** Hoy (`YYYY-MM-DD`) en la zona indicada. */
export function todayInZone(timeZone: string, now: Date = new Date()): string {
  return localDateInZone(now, timeZone);
}

/** Días del rango inclusive (`to − from + 1`); negativo o cero si el rango está invertido. */
export function rangeLength(from: string, to: string): number {
  return localDayDiff(from, to) + 1;
}

/** Día del mes con dos dígitos: `2026-09-02` → `02`. */
export function dayOfMonth(day: string): string {
  return day.slice(8, 10);
}

/** Abreviatura del día ISO (1 = lunes … 7 = domingo): `lu`, `ma`, … */
export function weekdayShort(weekday: number): string {
  return t(`schedules.weekday.short.${weekday}`);
}

/** Nombre del día ISO: `Lunes`, `Martes`, … */
export function weekdayLong(weekday: number): string {
  return t(`schedules.weekday.long.${weekday}`);
}

/**
 * Tolerancias de una versión: "Tolerancia de la empresa (5 min)" si no tiene propias; si no,
 * "Tardanza 10 min · Salida 0 min" (una propia en `null` usa la de la empresa). Las tolerancias de
 * empresa llegan en `me.company.settings`, que solo recibe quien tiene `company.settings.write`:
 * sin ellas (`null`) se dice "de la empresa" sin inventar minutos.
 */
export function toleranceLabel(
  version: { late_tolerance_minutes: number | null; early_leave_tolerance_minutes: number | null },
  companyLate: number | null,
  companyEarly: number | null,
): string {
  if (version.late_tolerance_minutes === null && version.early_leave_tolerance_minutes === null) {
    return companyLate === null
      ? t('schedules.templates.companyToleranceUnknown')
      : t('schedules.templates.companyTolerance', { minutes: companyLate });
  }
  const minutes = (own: number | null, company: number | null) =>
    own !== null
      ? t('schedules.format.minutes', { minutes: own })
      : company !== null
        ? t('schedules.format.minutes', { minutes: company })
        : t('schedules.templates.companyShort');
  return t('schedules.templates.ownTolerance', {
    late: minutes(version.late_tolerance_minutes, companyLate),
    early: minutes(version.early_leave_tolerance_minutes, companyEarly),
  });
}
